import { useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import { MobileDevice, SyncData } from '../../../../../types';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  UserKeys,
  exportMobileDevices,
  getAllUserMobileDevices,
} from '../../../../api/users-api';
import UserDeviceDialog from './device-details-dialog';
import DeleteUserDeviceDialog from './device-delete-dialog';
import BlockUserDeviceDialog from './device-block-dialog';
import WipeUserDeviceDialog from './device-wipe-dialog';
import WipeAccountUserDeviceDialog from './device-account-wipe-dialog';
import ApproveUserDeviceDialog from './device-approve-dialog';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import MobileManagementToolbar from './mobile-management-toolbar';
import dayjs from 'dayjs';
import { Messages } from '../../../../utils/message-constants';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../../shared/shared-data-grid-toolbar-helper';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { useStore } from '../../../state/zustand-store';
import { handleErrors } from '../../../../utils/errors';
import ChangeLogDialog from '../../../../shared/change-log-dialog';

import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Android from '@mui/icons-material/Android';
import Apple from '@mui/icons-material/Apple';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import DeleteSweep from '@mui/icons-material/DeleteSweep';
import DoNotDisturb from '@mui/icons-material/DoNotDisturb';
import FolderDelete from '@mui/icons-material/FolderDelete';
import Google from '@mui/icons-material/Google';
import Pending from '@mui/icons-material/Pending';
import PhoneIphoneRounded from '@mui/icons-material/PhoneIphoneRounded';
import Smartphone from '@mui/icons-material/Smartphone';

interface MobileDeviceGridProps {
  userEmail?: string;
  userRoles: string[];
  toolsRoles: string[];
  changeLogRoles: string[];
}

export default function MobileDeviceGrid({
  userEmail,
  toolsRoles,
  changeLogRoles,
}: MobileDeviceGridProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const { viewType } = useUserSettings();
  const [openDeleteDevice, setOpenDeleteDevice] = useState(false);
  const [openBlockDevice, setOpenBlockDevice] = useState(false);
  const [openApproveDevice, setOpenApproveDevice] = useState(false);
  const [openWipeDevice, setOpenWipeDevice] = useState(false);
  const [openWipeAccountDevice, setOpenWipeAccountDevice] = useState(false);
  const [openViewDeviceDetails, setOpenViewDeviceDetails] = useState(false);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<MobileDevice>();
  const [openExportChangeLog, setOpenExportChangeLog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const {
    data: devices,
    isLoading: devicesLoading,
    isFetching: devicesFetching,
  } = useQuery({
    queryKey: [UserKeys.Devices, userEmail],
    queryFn: () => getAllUserMobileDevices(userEmail ? userEmail : '*'),
    meta: {
      errorMessage: Messages.Errors.Network.Loading(
        `the user's mobile devices`
      ),
    },
  });

  const { mutate: handleExportMutate, isPending: isExporting } = useMutation({
    mutationFn: ({ changeLog }: { changeLog: string }) =>
      exportMobileDevices(changeLog),
    onSuccess: (spreadsheetId) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Mobile devices has been successfully exported.',
      });
      window.open(`https://docs.google.com/spreadsheets/d/${spreadsheetId}`);
      setOpenExportChangeLog(false);
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: 'There was an issue exporting mobile devices.',
      });
    },
  });

  const columns: GridColDef<MobileDevice>[] = useMemo(
    () => [
      {
        field: 'model',
        headerName: 'Device Name',
        flex: 2,
        pinnable: false,
        valueGetter: (value: string, row: MobileDevice) => {
          return `${row.name?.[0]?.split(' ')[0] ?? 'User'}'s ${value || ''}`;
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 2,
        pinnable: false,
        valueGetter: (value: string[]) => {
          return value?.[0] || '';
        },
      },
      {
        field: 'serialNumber',
        headerName: 'Serial Number',
        flex: 1,
        pinnable: false,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 2,
        pinnable: false,
        valueGetter: (value: string[]) => {
          return value?.[0] || '';
        },
      },
      {
        field: 'os',
        headerName: 'OS',
        flex: 1,
        pinnable: false,
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 20,
        pinnable: false,
        display: 'flex',
        type: 'singleSelect',
        valueOptions: [
          { label: 'iOS', value: 'IOS_SYNC' },
          { label: 'Android', value: 'ANDROID' },
          { label: 'Google', value: 'GOOGLE_SYNC' },
        ],
        renderCell: (params) => {
          if (params.value === 'IOS_SYNC') {
            return (
              <Tooltip title={params.value}>
                <Apple
                  key={params.value}
                  fontSize="small"
                  sx={{ color: '#7D7D7D', width: 'fit-content' }}
                />
              </Tooltip>
            );
          } else if (params.value === 'ANDROID') {
            return (
              <Tooltip title={params.value}>
                <Android
                  key={params.value}
                  fontSize="small"
                  sx={{ color: '#A4C639' }}
                />
              </Tooltip>
            );
          } else if (params.value === 'GOOGLE_SYNC') {
            return (
              <Tooltip title={params.value}>
                <Google
                  key={params.value}
                  fontSize="small"
                  sx={{ color: '#4285F4' }}
                />
              </Tooltip>
            );
          } else {
            return (
              <Tooltip title={params.value}>
                <Smartphone
                  key={params.value}
                  fontSize="small"
                  sx={{ color: 'disabled' }}
                />
              </Tooltip>
            );
          }
        },
      },
      {
        field: 'isForCompanyUseOnly',
        headerName: 'Ownership',
        type: 'singleSelect',
        valueOptions: [
          { label: 'Company owned', value: 'Company Owned' },
          { label: 'User owned', value: 'User Owned' },
        ],
        width: 130,
        pinnable: false,
        valueGetter: (value: boolean) => {
          if (value) {
            return 'Company Owned';
          } else {
            return 'User Owned';
          }
        },
      },
      {
        field: 'firstSync',
        headerName: 'First Sync',
        type: 'date',
        width: 110,
        pinnable: false,
        valueGetter: (value: SyncData) => {
          const date = new Date(value.value);
          return date;
        },
        valueFormatter: (value?: Date) => {
          const differenceInDays = dayjs().diff(dayjs(value), 'day');
          if (differenceInDays === 0) {
            const differenceInHours = dayjs().diff(dayjs(value), 'hour');
            if (differenceInHours === 0) {
              const differenceInMinutes = dayjs().diff(dayjs(value), 'minute');
              if (differenceInMinutes === 0) {
                return 'Just now';
              } else {
                return `${differenceInMinutes} minutes ago`;
              }
            } else {
              return `${differenceInHours} hours ago`;
            }
          } else {
            return `${differenceInDays} days ago`;
          }
        },
      },
      {
        field: 'lastSync',
        headerName: 'Last Sync',
        type: 'date',
        width: 110,
        pinnable: false,
        valueGetter: (value: SyncData) => {
          const date = new Date(value.value);
          return date;
        },
        valueFormatter: (value?: Date) => {
          const differenceInDays = dayjs().diff(dayjs(value), 'day');
          if (differenceInDays === 0) {
            const differenceInHours = dayjs().diff(dayjs(value), 'hour');
            if (differenceInHours === 0) {
              const differenceInMinutes = dayjs().diff(dayjs(value), 'minute');
              if (differenceInMinutes === 0) {
                return 'Just now';
              } else {
                return `${differenceInMinutes} minutes ago`;
              }
            } else {
              return `${differenceInHours} hours ago`;
            }
          } else {
            return `${differenceInDays} days ago`;
          }
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        pinnable: false,
        type: 'singleSelect',
        valueOptions: [
          { label: 'approved', value: 'APPROVED' },
          { label: 'blocked', value: 'BLOCKED' },
          { label: 'accounted wiped', value: 'UNKNOWN' },
          { label: 'wiped', value: 'WIPED' },
          { label: 'pending', value: 'PENDING' },
        ],
        renderCell: (params) => {
          const status = params.value || 'UNKNOWN';

          if (status === 'APPROVED') {
            return (
              <Chip
                variant="outlined"
                icon={<Check />}
                label="approved"
                color="success"
                size="small"
              />
            );
          } else if (status === 'BLOCKED') {
            return (
              <Chip
                variant="outlined"
                icon={<DoNotDisturb />}
                label={'blocked'}
                color={'warning'}
                size="small"
              />
            );
          } else if (status === 'UNKNOWN') {
            return (
              <Chip
                variant="outlined"
                icon={<FolderDelete />}
                label={'account wiped'}
                color={'error'}
                size="small"
              />
            );
          } else if (status === 'WIPED') {
            return (
              <Chip
                variant="outlined"
                icon={<DeleteSweep />}
                label={'wiped'}
                size="small"
                color="error"
              />
            );
          } else if (status === 'PENDING') {
            return (
              <Chip
                variant="outlined"
                icon={<Pending />}
                label={'pending'}
                size="small"
                color="warning"
              />
            );
          } else {
            return (
              <Chip
                variant="outlined"
                icon={<Close />}
                label={status.toLowerCase()}
                size="small"
                color="info"
              />
            );
          }
        },
      },
    ],
    []
  );

  const selectedDevices = useMemo(
    () =>
      devices?.filter(
        (device) => selectedDeviceIds?.includes(device.resourceId)
      ) || [],
    [devices, selectedDeviceIds]
  );

  const defaultGridState = {
    columns: {
      columnVisibilityModel: {
        serialNumber: false,
      },
    },
  };

  const toolbarProps = useMemo(
    () => ({
      selectedDevices,
      toolsRoles,
      setOpenDeleteDevice,
      setOpenApproveDevice,
      setOpenBlockDevice,
      setOpenWipeDevice,
      setOpenWipeAccountDevice,
      allDevices: devices || [],
      handleExportMutate,
      isExporting,
      setOpenExportChangeLog,
      changeLogRoles,
    }),
    [selectedDevices, toolsRoles, devices, changeLogRoles, isExporting]
  );

  return (
    <>
      <SharedDataGridClient
        checkboxSelection
        selectedRows={selectedDeviceIds}
        setSelectedRows={setSelectedDeviceIds}
        getRowId={(row) => row.resourceId}
        defaultGridState={defaultGridState}
        slots={{
          toolbar: useToolbar(MobileManagementToolbar, toolbarProps),
          ...createGridOverlays('Mobile Devices', <PhoneIphoneRounded />),
        }}
        disableRowSelectionOnClick
        rows={devices || []}
        onRowClick={(params) => {
          setSelectedDevice(params.row);
          setOpenViewDeviceDetails(true);
          createEvent(
            analytics.user_management.user_details.devices.viewed_mobile_device,
            {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.mobileDevices,
            }
          );
        }}
        columns={columns}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        loading={devicesLoading || devicesFetching}
        gridKey={viewType.mobileManagementGridStates?.activeGridState}
        gridStateKey="mobileManagementGridStates"
      />

      {openViewDeviceDetails && selectedDevice ? (
        <UserDeviceDialog
          open={openViewDeviceDetails}
          handleClose={() => setOpenViewDeviceDetails(false)}
          selectedDevice={selectedDevice}
        />
      ) : null}
      {openDeleteDevice && selectedDevices.length ? (
        <DeleteUserDeviceDialog
          open={openDeleteDevice}
          handleClose={() => setOpenDeleteDevice(false)}
          selectedDevices={selectedDevices}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
      {openApproveDevice && selectedDevices.length ? (
        <ApproveUserDeviceDialog
          open={openApproveDevice}
          handleClose={() => setOpenApproveDevice(false)}
          selectedDevices={selectedDevices}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
      {openBlockDevice && selectedDevices.length ? (
        <BlockUserDeviceDialog
          open={openBlockDevice}
          handleClose={() => setOpenBlockDevice(false)}
          selectedDevices={selectedDevices}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
      {openWipeDevice && selectedDevices.length ? (
        <WipeUserDeviceDialog
          open={openWipeDevice}
          handleClose={() => setOpenWipeDevice(false)}
          selectedDevices={selectedDevices}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
      {openWipeAccountDevice && selectedDevices.length ? (
        <WipeAccountUserDeviceDialog
          open={openWipeAccountDevice}
          handleClose={() => setOpenWipeAccountDevice(false)}
          selectedDevices={selectedDevices}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          setSelectedDeviceIds={setSelectedDeviceIds}
        />
      ) : null}
      <ChangeLogDialog
        open={openExportChangeLog}
        handleClose={() => setOpenExportChangeLog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() =>
          handleExportMutate({
            changeLog: changeLogMessage,
          })
        }
        mutateLoading={isExporting}
      />
    </>
  );
}
